import { motion } from 'motion/react';
import { Link } from 'react-router';
import { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import MarketingNav from '../components/MarketingNav';
import MarketingFooter from '../components/MarketingFooter';
import { ChevronRight, Layers, Calendar, FileText, BrainCircuit, BookOpen, Users, Quote, Wallet } from 'lucide-react';
import { HowItWorksRealLife } from '../components/HowItWorksRealLife';
import { appUrl } from '../lib/domains';
import { resolvePostAuthPath } from '../lib/legalAcceptance';

export default function Landing() {
  const { user, userData } = useAuth();
  
  useEffect(() => {
    if (user && userData !== null) {
      window.location.replace(appUrl(resolvePostAuthPath(userData)));
    }
  }, [user, userData]);

  return (
    <div className="min-h-screen bg-[#fafafa] text-zinc-900 font-sans selection:bg-zinc-900 selection:text-white overflow-x-hidden">
      <MarketingNav />

      {/* Hero Section */}
      <section className="pt-40 pb-20 px-6 max-w-5xl mx-auto text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-zinc-100 border border-zinc-200 text-xs font-semibold tracking-wide uppercase text-zinc-600 mb-8"
        >
          The future of legal practice in Pakistan
        </motion.div>

        <motion.h1 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="text-5xl md:text-7xl font-bold tracking-tight text-zinc-900 leading-[1.1] mb-8 text-balance"
        >
          Legal practice <br className="hidden md:block" />
          has been broken <br className="hidden md:block" />
          for decades.
          <span className="text-zinc-400"> We're fixing it.</span>
        </motion.h1>

        <motion.p 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="text-lg md:text-xl text-zinc-500 leading-relaxed max-w-3xl mx-auto mb-10"
        >
          Pakistani lawyers run some of the most complex litigation in the world, across multiple courts, under real pressure, with real stakes, all on a physical diary, a WhatsApp group, and a prayer. LexGat ends that. One platform. Every case. Total clarity.
        </motion.p>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16"
        >
          <a
            href={appUrl('/login')}
            className="w-full sm:w-auto bg-zinc-900 text-white px-8 py-4 rounded-full font-medium hover:bg-zinc-800 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 text-base inline-flex items-center justify-center"
          >
            Get started free
          </a>
          <Link
            to="/how-it-works"
            discover="none"
            className="w-full sm:w-auto text-zinc-900 bg-white border border-zinc-200 px-8 py-4 rounded-full font-medium hover:bg-zinc-50 transition-all flex items-center justify-center gap-2 text-base"
          >
            See what it does <ChevronRight className="w-4 h-4 text-zinc-400" />
          </Link>
          <Link
            to="/statutes"
            discover="none"
            className="w-full sm:w-auto text-zinc-900 bg-white border border-zinc-200 px-8 py-4 rounded-full font-medium hover:bg-zinc-50 transition-all text-base inline-flex items-center justify-center"
          >
            Browse statutes
          </Link>
        </motion.div>
      </section>

      <HowItWorksRealLife />




      {/* Testimonial Section */}
      <section className="py-32 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-zinc-900 mb-16 text-center text-balance">
            What happens when lawyers actually use it.
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                quote: "I had never seen my entire practice in one place before. Sixty-two cases. Every next date. Every current stage. In one morning. Eleven years into my career.",
                author: "Senior Advocate, Peshawar High Court"
              },
              {
                quote: "The bail application template filled in everything except the grounds. I spent the whole drafting time on the actual law. That's what it should be.",
                author: "Advocate, Sessions Courts Lahore"
              },
              {
                quote: "We stopped our daily morning meeting. I open the dashboard and I can see what every lawyer in my firm is working on, what's due this week, and what needs my attention. It took me fifteen years to have this visibility.",
                author: "Senior Partner, Karachi"
              }
            ].map((testimonial, i) => (
              <div key={i} className="bg-white rounded-3xl p-8 border border-zinc-200/80 shadow-sm flex flex-col justify-between">
                <div>
                  <Quote className="w-8 h-8 text-zinc-200 mb-6" />
                  <p className="text-lg text-zinc-800 leading-relaxed font-medium mb-8">
                    "{testimonial.quote}"
                  </p>
                </div>
                <div className="text-sm font-semibold text-zinc-500 uppercase tracking-widest border-t border-zinc-100 pt-6 mt-auto">
                  {testimonial.author}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final Hero CTA */}
      <section className="py-32 px-6 bg-zinc-50 border-t border-zinc-200">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl md:text-6xl font-bold tracking-tight text-zinc-900 mb-8 text-balance">
            Stop managing your practice like it's 1998.
          </h2>
          <p className="text-xl text-zinc-500 mb-12 leading-relaxed">
            Pakistani lawyers are among the sharpest legal minds in the world. They deserve infrastructure that matches. LexGat is that infrastructure. Free for your first 30 days. Set up in an afternoon.
          </p>
          <a
            href={appUrl('/login')}
            className="bg-zinc-900 text-white px-10 py-5 rounded-full font-medium hover:bg-zinc-800 transition-all shadow-xl hover:shadow-2xl hover:-translate-y-1 text-lg inline-block"
          >
            Start now, it's free
          </a>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
